import anime, {AnimeInstance, AnimeTimelineInstance} from 'animejs';
import Scene from '../classes/scene';
import SceneManager from '../classes/scene-manager';
import {ScrollDirection} from '../classes/scroll-manager';
import {getEl, getEls, isGreaterThanOrEqualTo, removeInlineStyles} from '../helpers';
import {ScrollAnimation} from "../components/scrolllocked-animations";
import ResponsiveWatcher, {ResponsiveSize} from "../classes/responsive-watcher";
import Globals from "../classes/globals";

export default class Circularity extends Scene {
	
	isMobile: boolean = false;
	
	intro: AnimeTimelineInstance = anime.timeline({
		easing: 'easeOutSine',
		duration: 4500,
		autoplay: false,
		delay: 0,
	});
	
	introexit: AnimeTimelineInstance = anime.timeline({
		easing: 'easeInOutSine',
		duration: 3000,
		autoplay: false,
		delay: 0,
	});
	
	
	mainTimeline: AnimeTimelineInstance = anime.timeline({
		easing: 'easeInOutSine',
		duration: 10000,
		autoplay: false,
		delay: 0,
	});
	
	mainScrollAnim: ScrollAnimation;
	introScrollAnim: ScrollAnimation;
	
	scrollTriggers: { animIn: AnimeInstance, animMobile: AnimeInstance, animOut: AnimeInstance, progress: number, active: boolean }[] = [];
	activeItem: number = -1;
	scrollTriggerTimer: number;
	
	actualMainCircleCircum: number = Circularity.MAIN_CIRCLE_RADIUS * Math.PI * 2;
	actualMainCircleRad: number = Circularity.MAIN_CIRCLE_RADIUS;
	
	targetDashOffset: number = 0;
	actualDashOffset: number = 0;
	
	fillCircleEl: SVGCircleElement;
	baseCircleEl: SVGCircleElement;
	
	scrollToContinueShown: boolean = false;
	
	scrollToContinueIntroY: number = 0;
	scrollToContinueMainY: number = 0;
	
	observer: IntersectionObserver;
	
	//The % from the bottom where the scroll to continue div should position itself on each screensize.
	//First number is the intro position, the last number is the main position. (in percentage)
	static scrollToContinuePositions: {[key in ResponsiveSize]?:number[]} = {
		[ResponsiveSize.xxl]: [12, 5],
		[ResponsiveSize.xl]: [12, 5],
	}
	
	
	
	static sizeToCircleRadius: {[key in ResponsiveSize]?:number} = {
		[ResponsiveSize.xxl]: 350,
		[ResponsiveSize.xl]: 400,
		[ResponsiveSize.lg]: 500,
	};
	
	static ITEMS = 5;
	static MAIN_CIRCLE_RADIUS = 350;
	static INTRO_SCROLL_PORTION = 0.5;
	static MAIN_SCROLL_PORTION = 1 - Circularity.INTRO_SCROLL_PORTION;
	
	constructor(manager: SceneManager, targetContainer: string) {
		super(manager, targetContainer);
		
		this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
			threshold: 1
		});
		
		
		this.onFrame = this.onFrame.bind(this);
		this.fillCircleEl = getEl('#circ-main-circle-2') as SVGCircleElement;
		this.baseCircleEl = getEl('#circ-main-circle-1') as SVGCircleElement;
		const benchCircleEl = getEl('#circ-main-benchmark') as SVGCircleElement;
		this.actualMainCircleCircum = Circularity.MAIN_CIRCLE_RADIUS * Math.PI * 2;
		this.actualMainCircleRad = Circularity.MAIN_CIRCLE_RADIUS;
		benchCircleEl.setAttributeNS(null, 'r', this.actualMainCircleRad+'');
		(this.baseCircleEl as any).progress = 0;
		(this.fillCircleEl as any).progress = 0;
		
		const continueEl:HTMLElement = getEl('.c-scroll-to-continue') as HTMLElement;
		(continueEl as any).progress = 0;
		
		this.intro.add({
			targets: getEl('#circ-intro-circle-1'),
			filter: [{value: 'blur(20px)', duration: 0}, {value: 'blur(0px)', duration: 1000}],
			opacity: [{value: 0, duration: 0}, {value: 1, duration: 1000}, {value: 1, duration: 3000}, {value: 0,duration: 500}],
			r: [{value: '300', duration: 0}, {value: '1200', duration: 2500, easing: 'easeOutSine'}],
		}).add({
			targets: getEl('#circ-intro-circle-2'),
			filter: [{value: 'blur(20px)', duration: 0}, {value: 'blur(0px)', duration: 1000}],
			opacity: [{value: 0, duration: 0}, {value: 1, duration: 1000}],
			r: [{value: '1', duration: 0}, {value: '500', duration: 2000, easing: 'easeOutQuad'}],
		}, 500).add({
			targets: getEl('.c-intro-text'),
			opacity: [0, 1],
			duration: 800,
			easing: "easeInOutSine",
		}, 1600).add({
			targets: getEl('.ci-images'),
			opacity: [0, 1],
			easing: "easeInOutSine",
			duration: 800,
		}, 1600).add({
			targets: getEl('.c-principles'),
			opacity: [0, 1],
			translateY: ['45%', '0%'],
			translateX: ['-50%', '-50%'],
			duration: 800,
			delay: 800,
			begin: anim => {
				const els = getEls('.c-intro-text .anim-heading');
				for (let i = 0; i < els.length; i++) {
					els[i].dispatchEvent(new CustomEvent('play'));
				}
				setTimeout(() => {
					const els2 = getEls('.ci-images .image-frame');
					for (let i = 0; i < els2.length; i++) {
						els2[i].dispatchEvent(new CustomEvent('play'));
					}
				}, 500);
			}
		}, 1300).add({
			targets: getEl('.c-intro-text'),
			opacity: [1, 0],
			duration: 400,
		}).add({
			targets: getEl('.ci-images'),
			scale: [1, 3.5],
			opacity: [1, 0],
			easing: "easeInOutSine",
			duration: 1000,
		}, 4400).add({
			targets: getEl('#circ-intro-circle-2'),
			r: [{value: '500', duration: 0}, {value: '1200', duration: 1500, easing: 'easeInOutSine'}],
		}, 4500).add({
			targets: this.baseCircleEl,
			filter: [{value: 'blur(20px)', duration: 0}, {value: 'blur(0px)', duration: 1000}],
			opacity: [0, 1],
			progress: [{value: 0, duration: 0}, {
				value: 100,
				duration: 1000,
				easing: 'easeOutSine',
			}],
			update: () => {
				this.baseCircleEl.setAttributeNS(null, 'r', Math.round((this.baseCircleEl as any).progress/100 * this.actualMainCircleRad) + '');
				this.fillCircleEl.setAttributeNS(null, 'r', Math.round((this.baseCircleEl as any).progress/100 * this.actualMainCircleRad) + '');
			},
			duration: 400,
		}, 4600).add({
			targets: [getEl('#circ-main-circle-2'), getEl('.ci-main-container')],
			opacity: [0, 1],
			duration: 400,
		}, 5200).add({
			targets: getEl('#circ-intro-circle-2'),
			opacity: [1, 0],
			duration: 400,
		}, 4600).add({
			targets: getEl('.c-scroll-to-continue'),
			progress: [0, 1],
			update: () => {
				const t = (continueEl as any).progress;
				continueEl.style.bottom = (this.scrollToContinueIntroY*(1-t) + this.scrollToContinueMainY*t) + 'px';
			},
			duration: 400,
		}, 4600);
		
		
		
		this.targetDashOffset = this.actualMainCircleCircum;
		this.actualDashOffset = this.actualMainCircleCircum;
		
		
		this.fillCircleEl.setAttributeNS(null, 'stroke-dasharray', this.actualMainCircleCircum + '');
		this.fillCircleEl.setAttributeNS(null, 'stroke-dashoffset', this.actualMainCircleCircum + '');
		
		//Main timeline:
		//All animations are done outside of this function.
		this.mainTimeline.add({
			targets: this.fillCircleEl,
			progress: [0, 100],
			duration: 5000,
		});
		
		
		this.introScrollAnim = this.addScrollAnimation(
			this.intro,
			this.elContainer.querySelector('.circ-sticky-container'), 800,
			0, 		//Start at top of container
			Circularity.INTRO_SCROLL_PORTION, 	//Finish halfway through the container
			0,
			-1.2, 	//Finish the animation 1.2 screen heights before.
			0 		//Count from the top of the window.
		);
		
		this.mainScrollAnim = this.addScrollAnimation(
			this.mainTimeline,
			this.elContainer.querySelector('.circ-sticky-container'), 200,
			Circularity.INTRO_SCROLL_PORTION,	//Start at halfway through the container
			Circularity.INTRO_SCROLL_PORTION + Circularity.MAIN_SCROLL_PORTION, //End at the end of the container
			0,
			-0.5, 	//Finish the animation half a screenheight before.
			1		//Count from the bottom of the window
		);
		
		this.addAnimation({
			targets: getEl(".c-scroll-to-continue svg"),
			easing: "easeInOutSine",
			translateY: [{value: '0%', duration: 4000}, {value: '40%', duration: 400}, {value: '0%', duration: 400}],
			loop: true,
		});
		
		this.createScrollTriggers();
		
		//const animDebug = new AnimationDebugger(this.mainTimeline, getEl('.circ-sticky'));
		
		const spotlight1 = this.elContainer.querySelector('.circ-spotlight-1');
		const spotlight2 = this.elContainer.querySelector('.circ-spotlight-2');
		
		//SPOTLIGHT 1 ANIMATION
		const spotvals = [
			{
				el: getEl('.circ-spotlight-1 .spot-image-background img'),
				dx: 0,
				dy: 0,
				amount: 1,
			},
		]
		
		manager.app.spotlighter.addSpotlightAnimation(
			{
				targets: spotlight1.querySelector('.zoomer'),
				scale: [1, 1.2],
				duration: 2000,
				easing: "easeOutQuart",
			},
			spotlight1,
			200,
			(anim, delta) => {
				
				const xRatio = (anim.mouseX / anim.elWidth) - 0.5;
				const yRatio = (anim.mouseY / anim.elHeight) - 0.5;
				
				for (let i = 0; i < spotvals.length; i++) {
					const targetDx = xRatio * spotvals[i].amount;
					const targetDy = yRatio * spotvals[i].amount;
					spotvals[i].dx += (targetDx - spotvals[i].dx) * delta * anim.smoothSpeed;
					spotvals[i].dy += (targetDy - spotvals[i].dy) * delta * anim.smoothSpeed;
					
					// @ts-ignore
					spotvals[i].el.style.transform = 'translate(' + spotvals[i].dx + 'vw, ' + spotvals[i].dy + 'vw)';
				}
				
			},
			() => {
				spotlight1.querySelector(".spot-heading").dispatchEvent(new CustomEvent('play'));
			},
			true
		);
		
		//SPOTLIGHT 2 ANIMATION
		const spotvals2 = [
			{
				el: getEl('.circ-spotlight-2 .spot-image-background img'),
				dx: 0,
				dy: 0,
				amount: 1,
			},
		]
		
		manager.app.spotlighter.addSpotlightAnimation(
			{
				targets: spotlight2.querySelector('.zoomer'),
				scale: [1, 1.08],
				duration: 2000,
				easing: "easeOutQuart",
			},
			spotlight2,
			200,
			(anim, delta) => {
				
				const xRatio = (anim.mouseX / anim.elWidth) - 0.5;
				const yRatio = (anim.mouseY / anim.elHeight) - 0.5;
				
				for (let i = 0; i < spotvals2.length; i++) {
					const targetDx = xRatio * spotvals2[i].amount;
					const targetDy = yRatio * spotvals2[i].amount;
					spotvals2[i].dx += (targetDx - spotvals2[i].dx) * delta * anim.smoothSpeed;
					spotvals2[i].dy += (targetDy - spotvals2[i].dy) * delta * anim.smoothSpeed;
					
					// @ts-ignore
					spotvals2[i].el.style.transform = 'translate(' + spotvals2[i].dx + 'vw, ' + spotvals2[i].dy + 'vw)';
				}
				
			},
			() => {
				spotlight2.querySelector(".spot-heading").dispatchEvent(new CustomEvent('play'));
			},
			true
		);
		
		
		//Spotlight interaction:
		
		spotlight1.addEventListener('click', (e) => {
			e.preventDefault();
			manager.app.modaller.openModal('techlifecycle');
		});
		spotlight2.addEventListener('click', (e) => {
			e.preventDefault();
			manager.app.modaller.openModal('circularity');
		});
		
		
		
		
		this.onResponsiveChange(this.manager.app.responser.currentSize, this.manager.app.responser.currentSize, this.manager.app.responser);
		this.onResized();
		
	}
	
	onEnteredView(dir: ScrollDirection) {
		super.onEnteredView(dir);
	}
	
	onExitedView(dir: ScrollDirection) {
		super.onExitedView(dir);
	}
	
	
	onResized() {
		super.onResized();
		
		if (this.isMobile) return;
		
		const screenSize = getEl('.circ-sticky').getBoundingClientRect();
		const continueSize = getEl('.c-scroll-to-continue').getBoundingClientRect();
		const introSize = getEl('#circ-intro-benchmark').getBoundingClientRect();
		const mainSize = getEl('#circ-main-benchmark').getBoundingClientRect();
	
		
		
		this.scrollToContinueIntroY = (window.innerHeight - (-screenSize.y + introSize.bottom)) + (continueSize.height);
		this.scrollToContinueMainY = (window.innerHeight - ((-screenSize.y + mainSize.bottom) + continueSize.height)) * 0.5;

		const stc:HTMLElement = getEl('.c-scroll-to-continue') as HTMLElement;
		stc.style.bottom = (this.introScrollAnim.targetProgress < 100) ? this.scrollToContinueIntroY+'px' : this.scrollToContinueMainY+'px';
		
	}
	
	
	createScrollTriggers() {
		this.scrollTriggers = [];
		
		for (let i = 1; i <= Circularity.ITEMS; i++) {
			
			const mobileTimeline = anime.timeline({
				autoplay: false,
			});
			
			const introTimeline = anime.timeline({
				autoplay: false,
			});
			
			introTimeline.add({
				targets: getEl(".ci-item-" + i),
				opacity: [0, 1],
				duration: 700,
				easing: "easeOutSine"
			}, 0).add({
				targets: getEl(".ci-item-" + i + " .ci-item-heading"),
				translateY: ['30%', '0%'],
				opacity: 1,
				duration: 600,
				easing: "easeOutQuart",
			}, 0).add({
				targets: getEl(".ci-item-" + i + " p"),
				opacity: 1,
				duration: 800,
				easing: "easeOutQuad",
			}, 0);
			
			this.createItemIconIntroAnim(i, introTimeline);
			this.createItemIconIntroAnim(i, mobileTimeline);
			
			const iconEl: HTMLElement = getEl(".ci-item-" + i + " .ci-item-icon") as HTMLElement;
			iconEl.setAttribute('data-index', i+'');
			this.observer.observe(iconEl);
			
			this.scrollTriggers.push({
				animMobile: mobileTimeline,
				animIn: introTimeline,
				animOut: anime({
					autoplay: false,
					targets: getEls(".ci-item-" + i + ", .ci-item-" + i + " > *"),
					delay: anime.stagger(150, {direction: "reverse"}),
					opacity: [1, 0],
					duration: 500,
					easing: "easeInCubic",
				}),
				progress: ((i - 1) / Circularity.ITEMS),
				active: false,
			});
			
			const el = getEl(".ci-item-" + i);
			if (el) (el as HTMLElement).style.opacity = '0';
		}
	}
	
	createItemIconIntroAnim(item: number, timeline: AnimeTimelineInstance) {
		
		if (item == 1) {
			//Reduce animation
			timeline.add({
				targets: getEl(".ci-item-" + item + " .ci-item-icon"),
				opacity: 1,
				duration: 600,
				easing: "easeInOutSine"
			}, 0).add({
				targets: getEl(".ci-item-" + item + " .svg-inward-arrows"),
				scale: [
					{value: 0.8, duration: 0, easing: "easeInQuart"},
					{value: 1.25,	duration: 600,easing: "easeOutQuad"},
					{value: 0.6, duration: 400, easing: "easeInOutQuad"},
					{value: 1, duration: 600, easing: "easeInQuad"}],
				duration: 500,
				easing: "easeInOutQuart"
			}, 0).add({
				targets: getEl(".ci-item-" + item + " .svg-reduce-box"),
				scale: [
					{value: 1.35, duration: 0},
					{value: 1.20, duration: 870},
					{value: 0.8, duration: 300},
					{value: 1, duration: 600}],
				duration: 800,
				easing: "easeInOutQuad"
			}, 0);
			
		} else if (item == 2) {
			//Refresh
			timeline.add({
				targets: getEl(".ci-item-" + item + " .ci-item-icon"),
				rotate: ['180deg', '0deg'],
				opacity: 1,
				duration: 1200,
				easing: "easeInOutQuart"
			}, 0);
		} else if (item == 3) {
			//Refurbish wrench
			timeline.add({
				targets: getEl(".ci-item-" + item + " .ci-item-icon"),
				rotate: [
					{value: '0deg', duration: 0},
					{value: '-20deg', duration: 300, easing: "easeInQuad"},
					{value: '30deg', duration: 300, easing: "easeOutQuad"},
					{value: '0deg', duration: 500, easing: "easeInOutQuad"},
				],
				opacity: 1,
				duration: 1200,
				easing: "easeInOutQuart"
			}, 0);
		} else if (item == 4) {
			//Remanufacture Arm
			timeline.add({
				targets: getEl(".ci-item-" + item + " .ci-item-icon"),
				opacity: 1,
				duration: 600,
				easing: "easeInOutSine"
			}, 0).add({
				targets: getEl(".ci-item-" + item + " .svg_robot_pivot"),
				rotate: ['0deg', '-24deg', '0deg'],
				translateY: [0, -1.5, 0],
				duration: 1200,
				easing: "easeInOutQuart"
			}, 0);
		} else if (item == 5) {
			//Recycle
			timeline.add({
				targets: getEl(".ci-item-" + item + " .ci-item-icon"),
				rotate: ['0deg', '120deg'],
				opacity: 1,
				duration: 1200,
				easing: "easeInOutQuint"
			}, 0);
		} else {
			timeline.add({
				targets: getEl(".ci-item-" + item + " .ci-item-icon"),
				rotate: ['360deg', '0deg'],
				opacity: 1,
				duration: 800,
				easing: "easeOutSine"
			}, 0);
		}
		
		return timeline;
	}
	
	//Only used for mobile animations.
	onIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver)
	{
		if (!this.isMobile) return;
		
		entries.forEach((item) => {
			if (item.isIntersecting)
			{
			 if ((item.target as any).dataset && (item.target as any).dataset.hasOwnProperty('index'))
			 {
				 const ind = parseInt((item.target as any).dataset.index) - 1;
				 if (ind >= 0 && ind < this.scrollTriggers.length)
				 {
					 if (this.scrollTriggers[ind].animMobile.completed) {
						 if (this.scrollTriggers[ind].animMobile.remaining == 0) {
							 this.scrollTriggers[ind].animMobile.restart();
						 }
					 } else {
						 this.scrollTriggers[ind].animMobile.play();
					 }
				 }
				 
			 }
			}
		})
	}
	
	
	onScroll(direction: ScrollDirection, progress?: number, oldProgress?: number) {
		super.onScroll(direction, progress, oldProgress);
		
		
		
		const mainProgress = this.mainScrollAnim.targetProgress;
		let currentItem = Math.min(Math.floor(mainProgress / 100 * Circularity.ITEMS), Circularity.ITEMS - 1);
		if (mainProgress <= 0) currentItem = -1;
		
		
		if (this.isMobile) return;
		
		let targetDashOffset = this.actualMainCircleCircum;
		if (currentItem >= Circularity.ITEMS) targetDashOffset = 0;
		targetDashOffset = this.actualMainCircleCircum - ((this.actualMainCircleCircum / Circularity.ITEMS) * (currentItem + 1));
		
		if (this.targetDashOffset != targetDashOffset) {
			this.targetDashOffset = targetDashOffset;
			requestAnimationFrame(this.onFrame);
		}
		
		if ((!this.scrollToContinueShown && mainProgress < 100) || (this.scrollToContinueShown && mainProgress >= 100)) {
			this.scrollToContinueShown = !this.scrollToContinueShown;
			anime({
				targets: getEl(".c-scroll-to-continue"),
				easing: "easeInOutSine",
				opacity: this.scrollToContinueShown ? [0, 1] : [1, 0],
				duration: 600,
				autoplay: true,
			});
		}
		
		if (this.activeItem != currentItem) {
			clearTimeout(this.scrollTriggerTimer);
			let delay = 0;
			
			for (let i= 0; i < this.scrollTriggers.length; i++)
			{
				if (i == this.activeItem || i == currentItem) continue;
				if (this.scrollTriggers[i].animOut.remaining > 0 || this.scrollTriggers[i].animIn.remaining > 0) {
					this.scrollTriggers[i].animOut.pause();
					this.scrollTriggers[i].animIn.pause();
					for (let j = 0; j < this.scrollTriggers[i].animOut.animatables.length; j++)
					{
						(this.scrollTriggers[i].animOut.animatables[j].target as HTMLElement).style.opacity = '0';
					}
				}
			}
			
			if (this.activeItem >= 0 && this.activeItem < Circularity.ITEMS) {
				
				if (this.scrollTriggers[this.activeItem].active) {
					this.scrollTriggers[this.activeItem].active = false;
					this.scrollTriggers[this.activeItem].animOut.restart();
					delay = this.scrollTriggers[this.activeItem].animOut.duration * 0.8;
				}
			}
			
			this.activeItem = currentItem;
			
			if (currentItem >= 0 && currentItem < Circularity.ITEMS) {
				this.scrollTriggerTimer = setTimeout(() => {
					this.scrollTriggers[currentItem].animIn.restart();
					this.scrollTriggers[this.activeItem].active = true;
				}, delay);
			}
		}
		
	}
	
	
	onFrame() {
		if (Math.abs(this.actualDashOffset - this.targetDashOffset) <= 0.1) {
			return;
		}
		
		this.actualDashOffset += (this.targetDashOffset - this.actualDashOffset) * 0.06;
		
		this.fillCircleEl.setAttributeNS(null, 'stroke-dashoffset', this.actualDashOffset + '');
		requestAnimationFrame(this.onFrame);
	}
	
	onResponsiveChange(newSize: ResponsiveSize, oldSize: ResponsiveSize,responser: ResponsiveWatcher) {
		
		if (isGreaterThanOrEqualTo(newSize, ResponsiveSize.lg))
		{
			this.manager.app.scrolllocker.enableAnimation(this.mainScrollAnim);
			this.manager.app.scrolllocker.enableAnimation(this.introScrollAnim);
			if (this.isMobile) {
				this.isMobile = false;
				setTimeout(() => {
					this.setupDesktop();
				}, 50);
			}
		}
		else {
			this.manager.app.scrolllocker.disableAnimation(this.mainScrollAnim);
			this.manager.app.scrolllocker.disableAnimation(this.introScrollAnim);
			if (!this.isMobile) {
				this.isMobile = true;
				setTimeout(() => {
					this.setupMobile();
				}, 50);
			}
		}
		

		
		const mainCircRad:number = Circularity.sizeToCircleRadius[newSize] ?? Circularity.MAIN_CIRCLE_RADIUS;
		
		getEl('#circ-main-benchmark').setAttributeNS(null, 'r', mainCircRad + '');
		this.fillCircleEl.setAttributeNS(null, 'r', mainCircRad + '');
		this.baseCircleEl.setAttributeNS(null, 'r', mainCircRad + '');
		this.actualMainCircleRad = mainCircRad;
		this.actualMainCircleCircum = mainCircRad * Math.PI * 2;
		this.fillCircleEl.setAttributeNS(null, 'stroke-dasharray', this.actualMainCircleCircum + '');
		
		
		
	}
	
	
	
	
	setupMobile() {

		for (let i= 0 ; i < this.scrollTriggers.length; i++)
		{
			this.scrollTriggers[i].animIn.pause();
			this.scrollTriggers[i].animOut.pause();
		}
		
		removeInlineStyles('.circ-sticky-container');

		const introCirc1 = getEl('#circ-intro-circle-1');
		const introCirc2 = getEl('#circ-intro-circle-2');
		
		introCirc1.setAttributeNS(null,'style', 'filter: blur(10px); opacity: 0.3');
		introCirc1.setAttributeNS(null, 'r', '800');
		introCirc2.removeAttributeNS(null, 'filter');
		introCirc2.removeAttributeNS(null, 'style');
		introCirc2.setAttributeNS(null, 'r', '1200');
		
		
		getEl('.c-intro-text').removeAttribute('style');
	}

	setupDesktop() {
		this.mainScrollAnim.anim.seek(0);
		this.introScrollAnim.anim.seek(0);
		this.manager.app.scrolllocker.askForFrame();
		if (this.activeItem >= 0 && this.activeItem < Circularity.ITEMS)
		{
			this.scrollTriggers[this.activeItem].animIn.restart();
		}
		
		this.baseCircleEl.setAttributeNS(null, 'style','opacity: 0');
		
		
	}
	
}
